/**
 * XVT Shipment Approval — data framework.
 *
 * DESIGN: the raw payload served by `GET /api/dashboard/xvt-shipment` contains
 * ONLY the assessment records (the source of truth) plus a refresh timestamp.
 * Every aggregation the dashboard shows — the summary KPIs, per-supplier and
 * per-project stats, the decision breakdown, and the cumulative pass-rate
 * timeline — is DERIVED from those records at render time (see
 * `deriveXvtDashboard`). Storing only raw records keeps the backend minimal and
 * avoids the aggregates ever drifting out of sync with the underlying data.
 */

/** Approval outcome of a single assessment. */
export type XvtDecision = 'APPROVED' | 'REJECTED';

/**
 * Optional modifier attached to a decision:
 * - `Risk Advisory`  — approved, but with advisory risk flags to watch.
 * - `Waiver Applied` — approved despite out-of-spec results, via a waiver.
 * - `Missing Data`   — rejected because required measurements were absent.
 * - `null`           — a clean pass or a straight rejection with no modifier.
 */
export type XvtQualifier =
  | 'Risk Advisory'
  | 'Waiver Applied'
  | 'Missing Data'
  | null;

/** A single shipment-approval assessment — the source-of-truth record. */
export interface XvtAssessment {
  /** Stable unique id (monotonic; used for ordering and keys). */
  id: number;
  /** Assessment date, ISO calendar date `YYYY-MM-DD`. */
  date: string;
  /** Supplier name. */
  supplier: string;
  /** Project / program name. */
  project: string;
  /** Part numbers covered by this assessment. */
  parts: string[];
  /** Human-readable summary of what was assessed. */
  description: string;
  /** Approval outcome. */
  decision: XvtDecision;
  /** Decision modifier, or `null`. */
  qualifier: XvtQualifier;
  /** Total dimensions / characteristics measured. */
  measurements: number;
  /** Count of out-of-spec measurements. */
  oos: number;
  /** Count of advisory risk flags raised. */
  riskItems: number;
  /** Scenario / classification label (e.g. "OOS + Waiver"). */
  scenario: string;
}

/** Raw payload returned by `GET /api/dashboard/xvt-shipment`. */
export interface XvtShipmentRaw {
  /** ISO timestamp of the last data refresh. */
  lastUpdated: string;
  /** The assessment records — the only stored data. */
  assessments: XvtAssessment[];
}

/** Aggregated stats for one supplier or project (fully DERIVED). */
export interface XvtGroupStat {
  total: number;
  approved: number;
  rejected: number;
  /** Approval rate as a percent, rounded to one decimal. */
  passRate: number;
}

/** Top-level KPI summary (fully DERIVED). */
export interface XvtSummary {
  totalAssessments: number;
  approved: number;
  rejected: number;
  passRate: number;
  suppliersCount: number;
  projectsCount: number;
}

/** Raw records plus every derived aggregation the dashboard renders. */
export interface XvtDashboard {
  lastUpdated: string;
  summary: XvtSummary;
  suppliers: Record<string, XvtGroupStat>;
  projects: Record<string, XvtGroupStat>;
  assessments: XvtAssessment[];
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/** Aggregate assessments by a grouping key (supplier or project). */
function groupBy(
  assessments: XvtAssessment[],
  key: 'supplier' | 'project',
): Record<string, XvtGroupStat> {
  const map: Record<string, XvtGroupStat> = {};
  for (const a of assessments) {
    const name = a[key];
    const g = map[name] ?? { total: 0, approved: 0, rejected: 0, passRate: 0 };
    g.total += 1;
    if (a.decision === 'APPROVED') g.approved += 1;
    else g.rejected += 1;
    map[name] = g;
  }
  for (const g of Object.values(map)) {
    g.passRate = g.total ? round1((g.approved / g.total) * 100) : 0;
  }
  return map;
}

/** Derive all dashboard aggregations from the raw assessment records. */
export function deriveXvtDashboard(raw: XvtShipmentRaw): XvtDashboard {
  const assessments = raw.assessments ?? [];
  const approved = assessments.filter((a) => a.decision === 'APPROVED').length;
  const rejected = assessments.length - approved;
  const suppliers = groupBy(assessments, 'supplier');
  const projects = groupBy(assessments, 'project');

  return {
    lastUpdated: raw.lastUpdated,
    summary: {
      totalAssessments: assessments.length,
      approved,
      rejected,
      passRate: assessments.length
        ? round1((approved / assessments.length) * 100)
        : 0,
      suppliersCount: Object.keys(suppliers).length,
      projectsCount: Object.keys(projects).length,
    },
    suppliers,
    projects,
    assessments,
  };
}
